'use client';

import { motion } from 'framer-motion';
import { SAVINGS_GOAL, type SavingsScale } from '@/src/shared/lib/chart';
import { savingsRateText } from '@/src/shared/lib/figureFormat';
import { cn } from '@/src/shared/lib/shadcn';

const DASH = 'border-t-2 border-dashed border-foreground/60';

/** The goal's key over the chart: a dashed stroke and its name. */
export function SavingsGoalKey({ label }: { label: string }) {
  return (
    <div
      className="mb-3 flex items-center justify-end gap-2 text-[11px] font-medium text-muted-foreground"
      aria-hidden="true"
    >
      <span className={`w-5 ${DASH}`} />
      {label}
    </div>
  );
}

interface Props {
  scale: SavingsScale;
  locale: string;
  reduced: boolean;
}

/** The scale's rates as labelled rules, and the dashed goal line drawn in front of the bars. */
export function SavingsRateAxis({ scale, locale, reduced }: Props) {
  const share = (rate: number) => (rate / scale.hi) * 100;

  return (
    <>
      {scale.ticks.map((tick) => (
        <div
          key={tick}
          aria-hidden="true"
          className="absolute inset-x-0 flex h-0 items-center"
          style={{ bottom: `${share(tick)}%` }}
        >
          <span className="w-9 text-[11px] tabular-nums text-muted-foreground">
            {savingsRateText(tick, locale)}
          </span>
          <span className="flex-1 border-t border-border/60" />
        </div>
      ))}
      {/* In front of the bars: a bar reaching past it saved more than planned. */}
      <motion.div
        aria-hidden="true"
        className={`pointer-events-none absolute right-0 left-9 z-10 ${DASH}`}
        style={{ bottom: `${share(SAVINGS_GOAL)}%`, transformOrigin: 'left' }}
        initial={reduced ? false : { scaleX: 0 }}
        animate={{ scaleX: 1 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      />
    </>
  );
}

// The landing's axis: the short month name without its trailing period ("sept.").
const axisMonth = (month: string, locale: string) =>
  new Intl.DateTimeFormat(locale, { month: 'short', timeZone: 'UTC' })
    .format(new Date(`${month}-15T00:00:00Z`))
    .replace(/\.$/, '');

interface MonthAxisProps {
  /** The slots before the first month, named but empty. */
  empty: string[];
  months: string[];
  locale: string;
  /** Every n-th month keeps its name, counted back from the latest. */
  labelStep: number;
  active: number | null;
}

/** The month names under the columns, the latest one in bold. */
export function SavingsMonthAxis({ empty, months, locale, labelStep, active }: MonthAxisProps) {
  const last = months.length - 1;
  return (
    <div className="mt-2 flex pl-9" aria-hidden="true">
      {empty.map((month) => (
        <span key={month} className="min-w-0 flex-1 text-center text-[11px] text-muted-foreground">
          {axisMonth(month, locale)}
        </span>
      ))}
      {months.map((month, i) => (
        <span
          key={month}
          className={cn(
            'min-w-0 flex-1 text-center text-[11px] whitespace-nowrap',
            i === last ? 'font-semibold text-foreground' : 'text-muted-foreground',
          )}
        >
          {(last - i) % labelStep === 0 || i === active ? axisMonth(month, locale) : ''}
        </span>
      ))}
    </div>
  );
}
